import React, { useRef, useEffect, useState } from 'react';
import { TextField, InputAdornment, IconButton, CircularProgress } from '@mui/material';
import { Clear as ClearIcon } from '@mui/icons-material';
import { BarcodeIcon } from 'lucide-react';

interface BarcodeScannerProps {
  onScan: (barcode: string) => void | Promise<void>;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  loading?: boolean;
  clearAfterScan?: boolean;
  debounceMs?: number;
}

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
  onScan,
  disabled = false,
  placeholder = 'Scan or type barcode',
  autoFocus = true,
  loading = false,
  clearAfterScan = true,
  debounceMs = 300,
}) => {
  const [barcode, setBarcode] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus, isScanning]);

  const handleScan = async (value: string) => {
    if (!value.trim() || disabled || isScanning) return;

    setIsScanning(true);

    try {
      await onScan(value.trim());
      if (clearAfterScan) {
        setBarcode('');
      }
    } catch (error) {
      console.error('Barcode scan error:', error);
    } finally {
      setIsScanning(false);
      // Refocus input after scan
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBarcode(value);

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer (for auto-scan on paste)
    if (value.length > 5) {
      // Assume barcodes are at least 6 characters
      debounceTimerRef.current = setTimeout(() => {
        // Auto-trigger scan for pasted barcodes
        if (value.trim()) {
          handleScan(value);
        }
      }, debounceMs);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      handleScan(barcode);
    }
  };

  const handleClear = () => {
    setBarcode('');
    inputRef.current?.focus();
  };

  return (
    <TextField
      fullWidth
      inputRef={inputRef}
      value={barcode}
      onChange={handleChange}
      onKeyPress={handleKeyPress}
      placeholder={placeholder}
      disabled={disabled || isScanning}
      autoFocus={autoFocus}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <BarcodeIcon color={disabled ? 'disabled' : 'action'} />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            {(isScanning || loading) && <CircularProgress size={20} />}
            {barcode && !isScanning && !loading && (
              <IconButton size="small" onClick={handleClear} edge="end">
                <ClearIcon />
              </IconButton>
            )}
          </InputAdornment>
        ),
      }}
      sx={{
        '& .MuiInputBase-input': {
          fontSize: '1.1rem',
          fontFamily: 'monospace',
        },
      }}
    />
  );
};

export default BarcodeScanner;
